import React from "react";
import { FormattedMessage } from "react-intl";
import moment from "moment";
import { makeStyles, Typography } from "@material-ui/core";

import { CONTRACT_HIERARCHY_FILTER_KEYS } from "./filterKeys";

import {
  CONTRACT_DURATIONS,
  CONTRACT_SITUATIONS_DEFAULT,
  CONTRACT_TYPES,
  CUSTOMER_ROUTES,
  HierarchyViewTypeEnum,
  removeEmptyFields,
  SORT,
} from "../../../../../util";
import { getContractsByHierarchy } from "../../../../../actions";
import ContractByHierarchyFilter from "./ContractByHierarchyFilter";
import ContractReportTable from "../ContractReportTable";

import AnimatedCardContent from "../../../AnimatedCardContent";
import ErrorMessage from "../../../ErrorMessage";
import LoadingSpinner from "../../../LoadingSpinner";
import useGetCurrentUser from "../../../../../hooks/useGetCurrentUser";
import useFetchUsersBelow from "../../../../../hooks/useFetchUsersBelow";

const useStyles = makeStyles((theme) => ({
  textAlignCenter: {
    textAlign: "center",
  },
}));

const fetchContractsReport = async ({ skip = 0, take = 40, params }) => {
  const { users, dateColumn, startDate, endDate, ...filters } = params;

  const reportParams = {
    ...filters,
    skip,
    take,
    ...(dateColumn && {
      dateColumn,
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    }),
    userIds: users?.map((u) => u.id) ?? [],
  };
  const cleanedFilters = removeEmptyFields(reportParams);

  return getContractsByHierarchy(cleanedFilters);
};

const ContractsByHierarchyContext = ({
  contractSituations = CONTRACT_SITUATIONS_DEFAULT,
  hierarchyViewType = HierarchyViewTypeEnum.DIRECTLY,
  enableUserSelect,
  params,
  customerRoutePrefix = CUSTOMER_ROUTES.AGENT,
}) => {
  const classes = useStyles();
  const currentUser = useGetCurrentUser();

  const [filters, setFilters] = React.useState({
    [CONTRACT_HIERARCHY_FILTER_KEYS.USERS]: [],
    [CONTRACT_HIERARCHY_FILTER_KEYS.HIERARCHY_VIEW_TYPE]: hierarchyViewType,

    [CONTRACT_HIERARCHY_FILTER_KEYS.CONTRACT_SITUATIONS]: contractSituations,
    [CONTRACT_HIERARCHY_FILTER_KEYS.CONTRACT_MODALITIES]: 
      Object.values(CONTRACT_TYPES),
    [CONTRACT_HIERARCHY_FILTER_KEYS.CONTRACT_DURATIONS]: CONTRACT_DURATIONS,

    [CONTRACT_HIERARCHY_FILTER_KEYS.DATE_COLUMN]: "",
    [CONTRACT_HIERARCHY_FILTER_KEYS.START_DATE]: moment().startOf("month"),
    [CONTRACT_HIERARCHY_FILTER_KEYS.END_DATE]: moment().endOf("month"),

    [CONTRACT_HIERARCHY_FILTER_KEYS.ORDER_BY]: "contractId",
    [CONTRACT_HIERARCHY_FILTER_KEYS.SORT]: SORT.DESC,
  });

  const { users, loading, error } = useFetchUsersBelow(params);

  const updateFilter = React.useCallback((key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const hasUsers = React.useMemo(
    () => !!filters[CONTRACT_HIERARCHY_FILTER_KEYS.USERS]?.length,
    [filters],
  );

  return (
    <>
      <ContractByHierarchyFilter
        filters={filters}
        updateFilter={updateFilter}
        currentUser={currentUser}
        users={users}
        loadingUsers={loading}
        errorUsers={error}
        enableUserSelect={enableUserSelect}
        defaultSituations={contractSituations}
      />

      {loading ? (
        <LoadingSpinner
          marginTop={40}
          message={<FormattedMessage id="app.loading.fetching.users" />}
        />
      ) : error ? (
        <ErrorMessage>
          <FormattedMessage id={error} defaultMessage={error} />
        </ErrorMessage>
      ) : enableUserSelect && !hasUsers ? (
        <AnimatedCardContent raised={false}>
          <Typography
            color="primary"
            variant="h6"
            className={classes.textAlignCenter}
          >
            <FormattedMessage id="app.report.contract.byCustomer.filters" />
          </Typography>
        </AnimatedCardContent>
      ) : (
        <ContractReportTable
          currentUser={currentUser}
          fetchContractsReport={fetchContractsReport}
          filters={filters}
          setOrderBy={(value) =>
            updateFilter(CONTRACT_HIERARCHY_FILTER_KEYS.ORDER_BY, value)
          }
          setSort={(value) =>
            updateFilter(CONTRACT_HIERARCHY_FILTER_KEYS.SORT, value)
          }
          loadingAgentsOrUsers={loading}
          reportKey="reportContractByHierarchy"
          customerRoutePrefix={customerRoutePrefix}
        />
      )}
    </>
  );
};

export default ContractsByHierarchyContext;
